/**
 * Light/dark switching. The tokens themselves live in index.css; this only
 * flips `data-theme` on <html> and remembers the choice.
 *
 * Deliberately *not* stored in the shared `settings` table the way the desktop
 * app stores its theme: that value is server-side and global, so one operator
 * switching to light mode would flip everyone's UI. A browser preference
 * belongs to the browser.
 */

export type ThemeName = "dark" | "light";

const STORAGE_KEY = "backoffice.theme";

export function readStoredTheme(): ThemeName {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light") return stored;
  // No explicit choice yet — follow the OS, defaulting to dark like the app.
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function applyTheme(theme: ThemeName): void {
  document.documentElement.dataset.theme = theme;
  // Keeps the mobile browser chrome in step with the page background.
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute("content", theme === "dark" ? "#060E19" : "#F2F5F9");
  localStorage.setItem(STORAGE_KEY, theme);
}

/** Resolve a palette token to its current value — for charts, which need real
 * colour strings rather than CSS variables. */
export function themeColor(token: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(token).trim();
}
